import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Title,
  Text,
  Loader,
  Group,
  Alert,
  Button,
  Select,
  Card,
  ThemeIcon,
  Badge,
  SimpleGrid,
  ActionIcon,
  Tooltip,
  Tabs,
  Modal,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconFileText,
  IconPlus,
  IconUpload,
  IconPencil,
  IconAdjustments,
  IconTrash,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import UploadAgreementModal, {
  EditAgreementModal,
} from "./UploadAgreementModal";
import ScalesSection from "./ScalesSection";
import AddScaleModal from "./AddScaleModal";
import CreateCustomDealModal from "./CreateCustomDealModal";
import UpfrontPayTypesList from "../Company/UpfrontPayTypesList";
import UpfrontPayScheduleSection from "../Company/UpfrontPayScheduleSection";
import ExpenseTypesList from "../Company/ExpenseTypesList";
import BonusTypesList from "../Company/BonusTypesList";
import DocumentTextTypesList from "../Company/DocumentTextTypesList";
import "./OrganizationContent.css";

// The Pay Detail Defaults inner tab is persisted in the URL (alongside the main
// admin `tab` param) so a refresh restores the sub-tab the user was on.
const PAY_DETAILS_PARAM = "paydetails";
const PAY_DETAILS_TABS = [
  "agreements",
  "scales",
  "legaldocument",
  "upfront",
  "expenses",
  "bonuses",
];

// MD templates render purple, REP templates render green.
const getTemplateColor = template =>
  template.deal_type === "MD" ? "grape" : "green";

function TemplateCard({ template, onClick, onEdit, onDelete }) {
  const color = getTemplateColor(template);
  const hasActions = !!onEdit || !!onDelete;
  const clickable = !!onClick && !hasActions;

  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      onClick={clickable ? onClick : undefined}
      style={{ cursor: clickable ? "pointer" : undefined }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon color={color} variant="light" size={42} radius="md">
            <IconFileText size={22} />
          </ThemeIcon>
          <Stack gap={2}>
            <Group gap="xs">
              <Text fw={600}>{template.name}</Text>
              <Badge color={color} variant="light" size="sm">
                {template.deal_type}
              </Badge>
              {template.is_customizable && (
                <Tooltip label="Customizable">
                  <ThemeIcon
                    color="orange"
                    variant="light"
                    size={20}
                    radius="sm"
                  >
                    <IconAdjustments size={14} />
                  </ThemeIcon>
                </Tooltip>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {template.description}
            </Text>
          </Stack>
        </Group>
        {hasActions && (
          <Group gap="xs" wrap="nowrap">
            {onEdit && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={event => {
                  event.stopPropagation();
                  onEdit();
                }}
                aria-label="Edit template"
              >
                <IconPencil size={16} />
              </ActionIcon>
            )}
            {onDelete && (
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={event => {
                  event.stopPropagation();
                  onDelete();
                }}
                aria-label="Delete template"
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        )}
      </Group>
    </Card>
  );
}

function TemplateSection({
  title,
  templates,
  canAdd,
  onAdd,
  onSelect,
  onCreateCustom,
  onEdit,
  onDelete,
}) {
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={4}>{title}</Title>
        <Group gap="xs">
          {canAdd && (
            <Button
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={onAdd}
            >
              Add Deal Template
            </Button>
          )}
          {onCreateCustom && (
            <Button
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={onCreateCustom}
            >
              Add Deal Template
            </Button>
          )}
        </Group>
      </Group>
      {templates.length === 0 ? (
        <Text size="sm" c="dimmed">
          No templates.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onClick={onSelect ? () => onSelect(template) : undefined}
              onEdit={onEdit ? () => onEdit(template) : undefined}
              onDelete={onDelete ? () => onDelete(template) : undefined}
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}

// company_default_scales / my_default_scales come back as a flat array of
// scales, each tagged with a scale_type and carrying default_scale_lines.
// Group them by type and normalize the lines into the scale_lines shape the
// scale cards and deal selects expect.
const groupScalesByType = scales => {
  const out = { COMMISSION: [], OVERRIDE: [], MD: [] };
  (Array.isArray(scales) ? scales : []).forEach(scale => {
    const type = scale.scale_type;
    if (!out[type]) out[type] = [];
    out[type].push({
      ...scale,
      scale_lines: Array.isArray(scale.default_scale_lines)
        ? scale.default_scale_lines
        : scale.scale_lines || [],
    });
  });
  return out;
};

// Combine two scale maps (each keyed by COMMISSION/OVERRIDE/MD) into one,
// de-duplicating by scale id. Lets the manager pay-details step resolve scales
// referenced by a company template as well as the manager's own custom scales.
const mergeScalesByType = (a, b) => {
  const out = {};
  ["COMMISSION", "OVERRIDE", "MD"].forEach(type => {
    const seen = new Set();
    out[type] = [
      ...(Array.isArray(a?.[type]) ? a[type] : []),
      ...(Array.isArray(b?.[type]) ? b[type] : []),
    ].filter(scale => {
      const key = String(scale.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
  return out;
};

function AdminTabPayStructurePage() {
  const { user, getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Inner Pay Detail Defaults tab, persisted in the URL so a refresh restores
  // it. Falls back to the first tab for missing/unknown values.
  const payDetailsParam = searchParams.get(PAY_DETAILS_PARAM);
  const payDetailsTab = PAY_DETAILS_TABS.includes(payDetailsParam)
    ? payDetailsParam
    : "agreements";

  const handlePayDetailsTabChange = value => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(PAY_DETAILS_PARAM, value);
        return next;
      },
      { replace: true }
    );
  };

  const [sellingYears, setSellingYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [defaultScales, setDefaultScales] = useState({});
  const [myScales, setMyScales] = useState({});
  const [upfrontPay, setUpfrontPay] = useState([]);
  const [defaultExpenses, setDefaultExpenses] = useState([]);
  const [defaultBonuses, setDefaultBonuses] = useState([]);
  const [companyUpfrontTypes, setCompanyUpfrontTypes] = useState([]);
  const [companyBonusTypes, setCompanyBonusTypes] = useState([]);
  const [companyExpenseTypes, setCompanyExpenseTypes] = useState([]);
  const [documentTextTypes, setDocumentTextTypes] = useState([]);
  const [upfrontPaySchedule, setUpfrontPaySchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [uploadModalOpened, setUploadModalOpened] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [addScaleOpened, setAddScaleOpened] = useState(false);
  const [editingCompanyScale, setEditingCompanyScale] = useState(null);
  const [customDealModalOpened, setCustomDealModalOpened] = useState(false);
  const [editingCustomDeal, setEditingCustomDeal] = useState(null);
  // True when the wizard is prefilled from a company template but should save as
  // a new manager deal (POST /v1/ps/manager-deal) instead of editing.
  const [customDealManagerCreate, setCustomDealManagerCreate] = useState(false);
  // True when the manager is editing one of their own templates (PUT
  // /v1/ps/manager-deal/{id}).
  const [customDealManagerEdit, setCustomDealManagerEdit] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  const isManager = user?.role === "MANAGER";
  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";

  // Manager editing one of their own templates: open the same step-by-step
  // wizard prefilled from the full template shape (deal_users, embedded
  // scales/upfront/bonuses/expenses), saving as an edit to the manager deal.
  const handleSelectTemplate = template => {
    setEditingCustomDeal(template);
    setCustomDealManagerCreate(false);
    setCustomDealManagerEdit(true);
    setCustomDealModalOpened(true);
  };

  // Editing a company template reuses the multi-step add-deal-template modal,
  // prefilled with the template's data. The modal reads the full template shape
  // (deal_users, embedded scales/upfront/bonuses/expenses) directly.
  const handleEditCompanyTemplate = template => {
    setEditingCustomDeal(template);
    setCustomDealManagerCreate(false);
    setCustomDealManagerEdit(false);
    setCustomDealModalOpened(true);
  };

  // Both company and manager deal templates are persisted as default deals, so
  // they delete through the same endpoint.
  const handleConfirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeletingTemplate(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/ps/default-deal/${templateToDelete.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        showSuccess("Deal template deleted successfully!");
        setTemplateToDelete(null);
        fetchOverview();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete deal template"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete deal template");
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleOpenAddCustomDeal = () => {
    setEditingCustomDeal(null);
    setCustomDealManagerCreate(false);
    setCustomDealManagerEdit(false);
    setCustomDealModalOpened(true);
  };

  // The custom deal modal saves the deal itself; just close and refresh.
  const handleCustomDealSaved = () => {
    setCustomDealModalOpened(false);
    setEditingCustomDeal(null);
    setCustomDealManagerCreate(false);
    setCustomDealManagerEdit(false);
    fetchOverview();
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpened(true);
  };

  const fetchOverview = useCallback(
    async yearId => {
      try {
        setLoading(true);
        const jwt = await getVerifiedJWT();

        if (!jwt) {
          setError("Failed to fetch pay structure");
          return;
        }

        const response = await fetch(
          getApiEndpoint(
            yearId != null
              ? `/v1/ps/overview?selling_year_id=${yearId}`
              : "/v1/ps/overview"
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const data = (result && result.data) || result || {};
          const years = Array.isArray(data.selling_years)
            ? data.selling_years
            : Array.isArray(data)
              ? data
              : [];
          setSellingYears(years);
          setCompanyTemplates(
            Array.isArray(data.company_templates) ? data.company_templates : []
          );
          setMyTemplates(
            Array.isArray(data.my_templates) ? data.my_templates : []
          );
          setAgreements(
            Array.isArray(data.uploaded_document_templates)
              ? data.uploaded_document_templates
              : []
          );
          setDefaultScales(groupScalesByType(data.company_default_scales));
          setMyScales(groupScalesByType(data.my_default_scales));
          setUpfrontPay(
            Array.isArray(data.upfront_pay) ? data.upfront_pay : []
          );
          setDefaultExpenses(
            Array.isArray(data.default_expenses) ? data.default_expenses : []
          );
          setDefaultBonuses(
            Array.isArray(data.default_bonuses) ? data.default_bonuses : []
          );
          setCompanyUpfrontTypes(
            Array.isArray(data.ps_company_upfront_types)
              ? data.ps_company_upfront_types
              : []
          );
          setCompanyBonusTypes(
            Array.isArray(data.ps_company_bonus_types)
              ? data.ps_company_bonus_types
              : []
          );
          setCompanyExpenseTypes(
            Array.isArray(data.ps_company_expense_types)
              ? data.ps_company_expense_types
              : []
          );
          setDocumentTextTypes(
            Array.isArray(data.document_text_types)
              ? data.document_text_types
              : []
          );
          setUpfrontPaySchedule(data.upfront_pay_schedule ?? null);

          if (yearId != null) {
            setSelectedYearId(String(yearId));
          } else {
            // Only unlocked years are selectable, so default to one of those.
            const selectableYears = years.filter(y => !y.is_locked);
            const defaultYear =
              selectableYears.find(y => y.is_active) ||
              selectableYears[0] ||
              null;
            setSelectedYearId(defaultYear ? String(defaultYear.id) : null);
          }
          setError(null);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || "Failed to fetch pay structure");
        }
      } catch (err) {
        handleGenericError(err, "Failed to fetch pay structure");
        setError("Failed to fetch pay structure");
      } finally {
        setLoading(false);
      }
    },
    [getVerifiedJWT]
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>Pay Structure</Title>

        {!loading && !error && sellingYears.length > 0 && (
          <Select
            label="Selling Year"
            placeholder="Select selling year"
            data={sellingYears
              .filter(year => !year.is_locked)
              .map(year => ({
                value: String(year.id),
                label: year.name,
              }))}
            value={selectedYearId}
            onChange={value => {
              setSelectedYearId(value);
              fetchOverview(value);
            }}
            allowDeselect={false}
            w={220}
          />
        )}
      </Group>

      {loading && (
        <Group justify="center" py="xl">
          <Loader size="md" />
          <Text>Loading pay structure...</Text>
        </Group>
      )}

      {error && !loading && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          <Group justify="space-between">
            <Text>{error}</Text>
            <Button
              variant="outline"
              size="sm"
              leftSection={<IconRefresh size={16} />}
              onClick={fetchOverview}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {!loading && !error && (
        <>
          {isCompanyAdmin && (
            <TemplateSection
              title="Company Deal Templates"
              templates={companyTemplates}
              onEdit={handleEditCompanyTemplate}
              onDelete={setTemplateToDelete}
              onCreateCustom={handleOpenAddCustomDeal}
            />
          )}

          {isManager && (
            <TemplateSection
              title="My Deal Templates"
              templates={myTemplates}
              onCreateCustom={handleOpenAddCustomDeal}
              onEdit={handleSelectTemplate}
              onDelete={setTemplateToDelete}
            />
          )}

          {isCompanyAdmin && (
            <Stack gap="md" mt={48}>
              <Title order={4}>Pay Detail Defaults</Title>
              <Tabs value={payDetailsTab} onChange={handlePayDetailsTabChange}>
                <Tabs.List>
                  <Tabs.Tab value="agreements">Uploaded Agreements</Tabs.Tab>
                  <Tabs.Tab value="scales">Scales</Tabs.Tab>
                  <Tabs.Tab value="legaldocument">Legal Document Text</Tabs.Tab>
                  <Tabs.Tab value="upfront">Upfront Pay</Tabs.Tab>
                  <Tabs.Tab value="expenses">Expenses</Tabs.Tab>
                  <Tabs.Tab value="bonuses">Bonuses</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="agreements" pt="md">
                  <Stack gap="sm">
                    <Group justify="flex-end">
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconUpload size={14} />}
                        onClick={handleOpenUploadModal}
                      >
                        Upload Agreement
                      </Button>
                    </Group>
                    {agreements.length === 0 ? (
                      <Text size="sm" c="dimmed">
                        No agreements uploaded.
                      </Text>
                    ) : (
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                        {agreements.map((agreement, index) => (
                          <Card
                            key={agreement.id ?? `${agreement.name}-${index}`}
                            padding="sm"
                            radius="md"
                            withBorder
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Group
                                gap="xs"
                                wrap="nowrap"
                                style={{ minWidth: 0 }}
                              >
                                <IconFileText size={18} />
                                <Text size="sm" fw={600} truncate>
                                  {agreement.name ?? "Untitled Agreement"}
                                </Text>
                              </Group>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() => setEditingAgreement(agreement)}
                                aria-label="Edit agreement"
                              >
                                <IconPencil size={16} />
                              </ActionIcon>
                            </Group>
                          </Card>
                        ))}
                      </SimpleGrid>
                    )}
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="scales" pt="md">
                  <ScalesSection
                    scales={defaultScales}
                    onAdd={() => {
                      setEditingCompanyScale(null);
                      setAddScaleOpened(true);
                    }}
                    onEdit={scale => {
                      setEditingCompanyScale(scale);
                      setAddScaleOpened(true);
                    }}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="legaldocument" pt="md">
                  <DocumentTextTypesList
                    documentTextTypes={documentTextTypes}
                    onTypesChange={setDocumentTextTypes}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="upfront" pt="md">
                  <Stack gap="lg">
                    <Stack gap="sm">
                      <Title order={5}>Upfront Pay Schedule</Title>
                      <UpfrontPayScheduleSection
                        schedule={upfrontPaySchedule}
                        onScheduleChange={setUpfrontPaySchedule}
                        sellingYearId={selectedYearId}
                      />
                    </Stack>
                    <Stack gap="sm">
                      <Title order={5}>Upfront Pay Types</Title>
                      <UpfrontPayTypesList
                        companyTypes={companyUpfrontTypes}
                        onTypesChange={setCompanyUpfrontTypes}
                      />
                    </Stack>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="expenses" pt="md">
                  <ExpenseTypesList
                    companyTypes={companyExpenseTypes}
                    onTypesChange={setCompanyExpenseTypes}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="bonuses" pt="md">
                  <BonusTypesList
                    companyTypes={companyBonusTypes}
                    onTypesChange={setCompanyBonusTypes}
                  />
                </Tabs.Panel>
              </Tabs>
            </Stack>
          )}

          {isManager && (
            <ScalesSection
              scales={myScales}
              title="My Custom Scales"
              onAdd={() => {
                setEditingCompanyScale(null);
                setAddScaleOpened(true);
              }}
              addLabel="Add Custom Scale"
              onEdit={scale => {
                setEditingCompanyScale(scale);
                setAddScaleOpened(true);
              }}
            />
          )}
        </>
      )}

      <UploadAgreementModal
        opened={uploadModalOpened}
        onClose={() => setUploadModalOpened(false)}
        onUploaded={fetchOverview}
        sellingYearId={selectedYearId}
      />

      <EditAgreementModal
        opened={!!editingAgreement}
        onClose={() => setEditingAgreement(null)}
        onUpdated={fetchOverview}
        agreement={editingAgreement}
      />

      <AddScaleModal
        opened={addScaleOpened}
        onClose={() => {
          setAddScaleOpened(false);
          setEditingCompanyScale(null);
        }}
        onCreated={fetchOverview}
        sellingYearId={selectedYearId}
        scale={editingCompanyScale}
      />

      <CreateCustomDealModal
        opened={customDealModalOpened}
        onClose={() => {
          setCustomDealModalOpened(false);
          setEditingCustomDeal(null);
          setCustomDealManagerCreate(false);
          setCustomDealManagerEdit(false);
        }}
        agreements={agreements}
        onSaved={handleCustomDealSaved}
        sellingYearId={selectedYearId}
        scales={
          isManager ? mergeScalesByType(defaultScales, myScales) : defaultScales
        }
        upfrontPay={upfrontPay}
        defaultBonuses={defaultBonuses}
        defaultExpenses={defaultExpenses}
        companyUpfrontTypes={companyUpfrontTypes}
        companyBonusTypes={companyBonusTypes}
        companyExpenseTypes={companyExpenseTypes}
        deal={editingCustomDeal}
        managerCreate={customDealManagerCreate}
        managerEdit={customDealManagerEdit}
        companyTemplates={companyTemplates}
      />

      <Modal
        opened={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        title="Delete Deal Template"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete
            {templateToDelete?.name
              ? ` "${templateToDelete.name}"`
              : " this"}{" "}
            deal template? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setTemplateToDelete(null)}
              disabled={deletingTemplate}
            >
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleConfirmDeleteTemplate}
              loading={deletingTemplate}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default AdminTabPayStructurePage;
